import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { FileText, Upload, Sparkles, Download, Loader2, FileCheck, Trash2 } from 'lucide-react';
import { Button } from './ui/button';
import { Textarea } from './ui/textarea';
import { format } from 'date-fns';

export default function ICSummaryTab({ projectId }) {
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [icSummary, setIcSummary] = useState('');
  const [formatTemplate, setFormatTemplate] = useState('');
  const { getAuthHeaders, API } = useAuth();

  useEffect(() => {
    fetchFiles();
  }, [projectId]);

  const fetchFiles = async () => {
    try {
      const response = await axios.get(`${API}/files/${projectId}`, getAuthHeaders());
      setFiles(response.data);
    } catch (error) {
      console.error('Failed to fetch files:', error);
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const validExtensions = ['.pdf', '.xlsx', '.xls', '.csv', '.pptx', '.ppt', '.docx', '.doc'];
    const fileName = file.name.toLowerCase();
    const isValid = validExtensions.some(ext => fileName.endsWith(ext));

    if (!isValid) {
      alert('Please upload PDF, Excel, PowerPoint, Word, or CSV files');
      return;
    }

    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      await axios.post(`${API}/files/upload/${projectId}`, formData, {
        headers: {
          ...getAuthHeaders().headers,
          'Content-Type': 'multipart/form-data',
        },
      });
      fetchFiles();
      e.target.value = ''; // Reset input
    } catch (error) {
      console.error('Failed to upload file:', error);
      alert(error.response?.data?.detail || 'Failed to upload file. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  const generateICSummary = async () => {
    setGenerating(true);
    try {
      const response = await axios.post(
        `${API}/chat/generate-ic-summary/${projectId}`,
        { format_template: formatTemplate || null },
        getAuthHeaders()
      );
      setIcSummary(response.data.summary);
    } catch (error) {
      console.error('Failed to generate IC summary:', error);
      alert('Failed to generate summary. Please try again.');
    } finally {
      setGenerating(false);
    }
  };

  const copyToClipboard = () => {
    navigator.clipboard.writeText(icSummary);
    alert('Summary copied to clipboard!');
  };

  const downloadAsText = () => {
    const blob = new Blob([icSummary], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `IC-Summary-${new Date().toISOString().split('T')[0]}.txt`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div>
      <div className="mb-8">
        <h2 className="text-2xl font-heading font-bold text-slate-900 mb-2">IC Summary Generator</h2>
        <p className="text-sm text-slate-600">
          Upload company documents, provide a format template, and let Claude create a professional Investment Committee summary
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left Column: Upload & Format */}
        <div className="lg:col-span-1 space-y-6">
          {/* File Upload */}
          <div className="card p-6">
            <h3 className="text-lg font-heading font-semibold text-slate-900 mb-4 flex items-center gap-2">
              <Upload size={20} />
              Upload Documents
            </h3>
            
            <input
              type="file"
              accept=".pdf,.xlsx,.xls,.csv,.pptx,.ppt,.docx,.doc"
              onChange={handleFileUpload}
              disabled={uploading}
              className="hidden"
              id="file-upload"
            />
            <label htmlFor="file-upload">
              <Button
                as="span"
                disabled={uploading}
                className="btn-primary w-full cursor-pointer"
                data-testid="upload-file-button"
              >
                {uploading ? (
                  <>
                    <Loader2 size={18} className="mr-2 animate-spin" />
                    Uploading...
                  </>
                ) : (
                  <>
                    <Upload size={18} className="mr-2" />
                    Upload Document
                  </>
                )}
              </Button>
            </label>

            <p className="text-xs text-slate-500 mt-3">
              Upload any business document (PDF, Excel, PowerPoint, Word, CSV)
            </p>

            {/* Files List */}
            <div className="mt-4 space-y-2">
              {files.length === 0 ? (
                <p className="text-sm text-slate-500 text-center py-4">No files uploaded yet</p>
              ) : (
                files.map((file) => (
                  <div
                    key={file.id}
                    className="bg-slate-50 rounded-md p-3 text-sm"
                    data-testid="uploaded-file-item"
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex items-start gap-2 flex-1">
                        <FileCheck className="text-green-600 flex-shrink-0 mt-0.5" size={16} />
                        <div className="flex-1 min-w-0">
                          <div className="font-medium text-slate-900 truncate">{file.filename}</div>
                          <div className="flex items-center gap-2 mt-1">
                            <span className={`text-xs px-2 py-0.5 rounded ${
                              file.file_type === 'pdf' ? 'bg-red-100 text-red-700' :
                              file.file_type === 'excel' ? 'bg-green-100 text-green-700' :
                              file.file_type === 'powerpoint' ? 'bg-orange-100 text-orange-700' :
                              file.file_type === 'word' ? 'bg-blue-100 text-blue-700' :
                              'bg-slate-100 text-slate-700'
                            }`}>
                              {file.file_type === 'powerpoint' ? 'PPT' : 
                               file.file_type === 'word' ? 'WORD' : 
                               file.file_type.toUpperCase()}
                            </span>
                            <span className="text-xs text-slate-500">
                              {(file.file_size / 1024).toFixed(0)} KB · {format(new Date(file.uploaded_at), 'MMM dd')}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>

          {/* Format Template */}
          <div className="card p-6">
            <h3 className="text-lg font-heading font-semibold text-slate-900 mb-4 flex items-center gap-2">
              <FileText size={20} />
              Format Template (Optional)
            </h3>
            
            <Textarea
              value={formatTemplate}
              onChange={(e) => setFormatTemplate(e.target.value)}
              placeholder="Paste your IC memo format here, or leave blank for default format...

Example:
1. Company Overview
2. Market Opportunity
3. Traction & Metrics
4. Team Assessment
5. Investment Terms
6. Risks
7. Recommendation"
              rows={12}
              className="text-sm font-mono"
              data-testid="format-template-input"
            />
            
            <p className="text-xs text-slate-500 mt-3">
              Provide your preferred IC summary structure. Claude will follow this format.
            </p>
          </div>

          {/* Generate Button */}
          <Button
            onClick={generateICSummary}
            disabled={generating}
            className="btn-primary w-full flex items-center justify-center gap-2"
            data-testid="generate-ic-summary-button"
          >
            {generating ? (
              <>
                <Loader2 size={18} className="animate-spin" />
                Generating Summary...
              </>
            ) : (
              <>
                <Sparkles size={18} />
                Generate IC Summary
              </>
            )}
          </Button>
        </div>

        {/* Right Column: Generated Summary */}
        <div className="lg:col-span-2">
          <div className="card p-8 min-h-[600px]">
            {!icSummary ? (
              <div className="flex flex-col items-center justify-center h-full text-center py-20">
                <FileText className="text-slate-300 mb-4" size={64} />
                <h3 className="text-xl font-medium text-slate-900 mb-2">No IC Summary Generated Yet</h3>
                <p className="text-slate-600 max-w-md mb-6">
                  Upload company documents (PDFs, Excel, PowerPoint, Word, CSVs), optionally provide a format template, then click "Generate IC Summary" to create a professional Investment Committee memo.
                </p>
                <div className="text-sm text-slate-500 space-y-2 text-left max-w-md">
                  <p><strong>Claude will analyze:</strong></p>
                  <ul className="list-disc list-inside space-y-1">
                    <li>Dashboard insights (companies, metrics, trends)</li>
                    <li>Project summary and key findings</li>
                    <li>Uploaded documents (all formats)</li>
                    <li>All conversations and research chats</li>
                  </ul>
                  <p className="mt-3"><strong>Supported file types:</strong></p>
                  <div className="flex flex-wrap gap-2 mt-1">
                    <span className="bg-red-100 text-red-700 text-xs px-2 py-1 rounded">PDF</span>
                    <span className="bg-green-100 text-green-700 text-xs px-2 py-1 rounded">EXCEL</span>
                    <span className="bg-orange-100 text-orange-700 text-xs px-2 py-1 rounded">PPT</span>
                    <span className="bg-blue-100 text-blue-700 text-xs px-2 py-1 rounded">WORD</span>
                    <span className="bg-slate-100 text-slate-700 text-xs px-2 py-1 rounded">CSV</span>
                  </div>
                </div>
              </div>
            ) : (
              <>
                <div className="flex items-center justify-between mb-6">
                  <h3 className="text-xl font-heading font-semibold text-slate-900">Generated IC Summary</h3>
                  <div className="flex gap-2">
                    <Button
                      onClick={copyToClipboard}
                      size="sm"
                      className="btn-secondary"
                      data-testid="copy-summary-button"
                    >
                      <FileText size={16} className="mr-2" />
                      Copy
                    </Button>
                    <Button
                      onClick={downloadAsText}
                      size="sm"
                      className="btn-secondary"
                      data-testid="download-summary-button"
                    >
                      <Download size={16} className="mr-2" />
                      Download
                    </Button>
                    <Button
                      onClick={() => setIcSummary('')}
                      size="sm"
                      variant="ghost"
                      className="text-slate-500"
                    >
                      <Trash2 size={16} />
                    </Button>
                  </div>
                </div>

                <div className="prose prose-slate max-w-none" data-testid="ic-summary-content">
                  <div className="whitespace-pre-wrap text-slate-700 leading-relaxed">
                    {icSummary}
                  </div>
                </div>

                <div className="mt-8 p-4 bg-blue-50 border border-blue-200 rounded-md">
                  <p className="text-sm text-blue-900">
                    💡 <strong>Tip:</strong> You can regenerate the summary with different format templates or after adding more documents. Each generation considers all current project data.
                  </p>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
